import {
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { differenceInDays, format } from "date-fns";

import MDBadge from "components/MDBadge";
import MDBox from "components/MDBox";
import TagColorChip from "components/TagColorChip";
import { useMaterialUIController } from "context";
import { resolveTagColorHex } from "helpers/getColorsTags";
import { formatCurrency } from "helpers/utils";
import { ILotes } from "interfaces/lotes";
import { TipoTag } from "interfaces/typesystem";

import { LOTE_ACCENT, loteCardSx } from "./lotes-evento.theme";

interface Props {
  orderLote: number;
  lote: ILotes;
  selectedLote: (lote: ILotes) => void;
  removeLote: (lote: ILotes) => void;
}

export default function CardListLotes({
  orderLote,
  lote,
  selectedLote,
  removeLote,
}: Props) {
  const [controller] = useMaterialUIController();
  const catalogTags: TipoTag[] = controller.dataConfig?.tipoTags || [];

  const inicioLote = format(
    lote.dataInicio.slice(0, 10).concat(`T23:59:59.000Z`),
    "dd/MM/yyyy"
  );
  const finalLote = format(
    lote.dataFinal.slice(0, 10).concat(`T23:59:59.000Z`),
    "dd/MM/yyyy"
  );
  const valoresTags = lote.valoresTags || [];
  const dias = Math.max(
    0,
    differenceInDays(new Date(lote.dataFinal), new Date(lote.dataInicio))
  );

  const resolveCor = (tag: {
    id?: number;
    tipo: string;
    cor?: string | null;
  }) => {
    const fromCatalog = catalogTags.find(
      (item: TipoTag) =>
        (tag.id != null && item.id === tag.id) ||
        item.tipo.toLowerCase() === tag.tipo.toLowerCase()
    );
    return resolveTagColorHex(tag.tipo, fromCatalog?.cor ?? tag.cor);
  };

  return (
    <MDBox sx={loteCardSx}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        justifyContent="space-between"
      >
        <MDBox sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            mb={1.25}
          >
            <MDBadge
              variant="gradient"
              size="sm"
              badgeContent={`Lote ${orderLote}`}
              color="secondary"
              container
            />
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CalendarMonthOutlinedIcon
                sx={{ fontSize: 16, color: "text.secondary" }}
              />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {inicioLote} — {finalLote}
              </Typography>
            </Stack>
            <Chip
              size="small"
              label={`${dias} dia${dias === 1 ? "" : "s"}`}
              variant="outlined"
              sx={{
                height: 24,
                fontWeight: 600,
                borderColor: "rgba(249, 96, 48, 0.35)",
                color: LOTE_ACCENT,
                bgcolor: "rgba(249, 96, 48, 0.04)",
              }}
            />
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center" mb={1}>
            <LocalOfferOutlinedIcon
              sx={{ fontSize: 15, color: "text.secondary" }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
            >
              Tags e valores
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {valoresTags.length > 0 ? (
              valoresTags.map((tag, idx) => (
                <TagColorChip
                  key={`${tag.id}-${idx}`}
                  tipo={tag.tag.tipo}
                  cor={resolveCor(tag.tag)}
                  label={`${tag.tag.tipo} — ${formatCurrency(tag.valor)}`}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                Nenhuma tag definida neste lote
              </Typography>
            )}
          </Stack>
        </MDBox>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent={{ xs: "flex-end", sm: "flex-start" }}
        >
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="info"
              onClick={() => selectedLote(lote)}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              color="error"
              onClick={() => removeLote(lote)}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </MDBox>
  );
}
